import { reporting } from '../../../api';
import { call, takeLatest, put, delay } from 'redux-saga/effects';
import {
  GET_BRANCH_REPORT_DATA,
  GET_BRANCH_REPORT_DATA_START,
  GET_BRANCH_REPORT_DATA_COMPLETE,
  GET_BRANCH_REPORT_DATA_ERROR,
  GET_AGENCY_REPORT_GRAPH_DATA,
  GET_AGENCY_REPORT_GRAPH_DATA_START,
  GET_AGENCY_REPORT_GRAPH_DATA_COMPLETE,
  GET_AGENCY_REPORT_GRAPH_DATA_ERROR,
  DOWNLOAD_AGENCY_REPORT,
  DOWNLOAD_AGENCY_REPORT_START,
  DOWNLOAD_AGENCY_REPORT_COMPLETE,
  DOWNLOAD_AGENCY_REPORT_ERROR,
  GET_BRANCH_EMPLOYEES_DATA,
  GET_BRANCH_EMPLOYEES_DATA_START,
  GET_BRANCH_EMPLOYEES_DATA_COMPLETE,
  GET_BRANCH_EMPLOYEES_DATA_ERROR,
  GET_EMPLOYEE_DETAILS,
  GET_EMPLOYEE_DETAILS_START,
  GET_EMPLOYEE_DETAILS_COMPLETE,
  GET_EMPLOYEE_DETAILS_ERROR,
  GET_CARE_COIN_TRACKER_DATA,
  GET_CARE_COIN_TRACKER_DATA_START,
  GET_CARE_COIN_TRACKER_DATA_COMPLETE,
  GET_CARE_COIN_TRACKER_DATA_ERROR,
  SEND_CARE_COINS,
  SEND_CARE_COINS_START,
  SEND_CARE_COINS_COMPLETE,
  SEND_CARE_COINS_ERROR,
  GET_SEARCH_EMPLOYEES_DATA,
  GET_SEARCH_EMPLOYEES_DATA_START,
  GET_SEARCH_EMPLOYEES_DATA_COMPLETE,
  GET_SEARCH_EMPLOYEES_DATA_ERROR,
  EMPLOYEE_EARNING_FETCH,
  EMPLOYEE_EARNING_FETCH_START,
  EMPLOYEE_EARNING_FETCH_COMPLETE,
  EMPLOYEE_EARNING_FETCH_ERROR,
  RETRY_REDEEM_REWARD,
  RETRY_REDEEM_REWARD_START,
  RETRY_REDEEM_REWARD_COMPLETE,
  RETRY_REDEEM_REWARD_ERROR,
} from './actions';
import errorStrings from '../../../constants/errorStrings';
import { setUserStatusUnauthorised } from '../userProfile/actions';

export function* getBranchReportData({ payload: { pageNumber, pageSize, agency } }) {
  try {
    yield put({
      type: GET_BRANCH_REPORT_DATA_START,
    });
    const { response, error } = yield call(reporting.getBranchReportData, {
      pageNumber,
      pageSize,
      agency,
    });

    if (!!response) {
      yield put({
        type: GET_BRANCH_REPORT_DATA_COMPLETE,
        payload: {
          data: response.data,
          totalPages: response.total_pages,
          totalRecords: response.total_records,
        },
      });
    } else {
      //call logout
      if (error.status === 401) {
        yield put(setUserStatusUnauthorised());
      } else {
        yield put(
          onGetBranchReportDataError({
            error: error?.data.message ? error?.data.message : errorStrings.commonError,
          }),
        );
      }
    }
  } catch (error) {
    yield put(
      onGetBranchReportDataError({
        error: errorStrings.commonError,
      }),
    );
  }
}

export function* getAgencyReportGraphData({
  payload: { startDate, endDate, durationFilter, agency },
}) {
  try {
    yield put({
      type: GET_AGENCY_REPORT_GRAPH_DATA_START,
    });
    const { response, error } = yield call(reporting.getAgencyReportGraphData, {
      startDate,
      endDate,
      durationFilter,
      agency,
    });

    if (!!response) {
      yield put({
        type: GET_AGENCY_REPORT_GRAPH_DATA_COMPLETE,
        payload: {
          data: response?.data?.data || [],
          avgEarning: response?.data?.avg_earning || 0,
          avgRedemption: response?.data?.avg_redemption || 0,
          durationFilter: response?.data?.duration_filter || durationFilter,
        },
      });
    } else {
      if (error.status === 401) {
        yield put(setUserStatusUnauthorised());
      } else {
        yield put(
          onGetAgencyReportGraphDataError({
            error: error?.data.message ? error?.data.message : errorStrings.commonError,
          }),
        );
      }
    }
  } catch (error) {
    yield put(
      onGetAgencyReportGraphDataError({
        error: errorStrings.commonError,
      }),
    );
  }
}

export function* getBranchEmployeesData({ payload: { params, agency } }) {
  try {
    yield put({
      type: GET_BRANCH_EMPLOYEES_DATA_START,
    });

    const { response, error } = yield call(reporting.getBranchEmployeesData, {
      params,
      agency,
    });

    if (!!response) {
      yield put({
        type: GET_BRANCH_EMPLOYEES_DATA_COMPLETE,
        payload: {
          data: response.data,
          totalPages: response.total_pages,
          totalRecords: response.total_records,
        },
      });
    } else {
      //call logout
      if (error.status === 401) {
        yield put(setUserStatusUnauthorised());
      } else {
        yield put(
          onGetBranchEmployeesDataError({
            error: error?.data.message ? error?.data.message : errorStrings.commonError,
          }),
        );
      }
    }
  } catch (error) {
    yield put(
      onGetBranchEmployeesDataError({
        error: errorStrings.commonError,
      }),
    );
  }
}

export function* downloadAgencyReport({
  payload: { startDate, endDate, durationFilter, agency },
}) {
  try {
    yield put({
      type: DOWNLOAD_AGENCY_REPORT_START,
    });
    const { response, error } = yield call(reporting.downloadAgencyReport, {
      startDate,
      endDate,
      durationFilter,
      agency,
    });

    if (!!response) {
      const contentType = response.headers?.['content-type'] || '';
      const contentDisposition = response.headers?.['content-disposition'] || '';
      const fileNameFromHeaderMatch =
        contentDisposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        contentDisposition.match(/filename="?([^";]+)"?/i);
      const fileNameFromHeader = fileNameFromHeaderMatch?.[1]
        ? decodeURIComponent(fileNameFromHeaderMatch[1]).replaceAll('"', '')
        : '';
      const defaultExtension = contentType.includes('zip') ? 'zip' : 'xlsx';
      const timestamp = new Date().toISOString().replaceAll(':', '-');
      const agencyName = agency || 'agency';
      const aggregateType = durationFilter?.toLowerCase() || 'monthly';
      const fallbackFileName = `${timestamp}-${agencyName}-${aggregateType}-report.${defaultExtension}`;
      const fileName = fileNameFromHeader || fallbackFileName;

      const url = window.URL.createObjectURL(
        new Blob([response.data], {
          type: contentType || 'application/octet-stream',
        }),
      );

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      yield put({
        type: DOWNLOAD_AGENCY_REPORT_COMPLETE,
      });
    } else {
      if (error.status === 401) {
        yield put(setUserStatusUnauthorised());
      } else {
        yield put(
          onDownloadAgencyReportError({
            error: error?.data?.message ? error.data.message : errorStrings.commonError,
          }),
        );
      }
    }
  } catch (error) {
    yield put(
      onDownloadAgencyReportError({
        error: errorStrings.commonError,
      }),
    );
  }
}

export function* getEmployeeDetails({ payload: { caregiverId, agency } }) {
  try {
    yield put({
      type: GET_EMPLOYEE_DETAILS_START,
    });
    const { response, error } = yield call(reporting.getEmployeeDetails, {
      caregiverId,
      agency,
    });

    if (!!response) {
      yield put({
        type: GET_EMPLOYEE_DETAILS_COMPLETE,
        payload: { data: response.data[0], legend: response.legend },
      });
    } else {
      //call logout
      if (error.status === 401) {
        yield put(setUserStatusUnauthorised());
      } else {
        yield put(
          onGetEmployeeDetailsError({
            error: error?.data.message ? error?.data.message : errorStrings.commonError,
          }),
        );
      }
    }
  } catch (error) {
    yield put(
      onGetEmployeeDetailsError({
        error: errorStrings.commonError,
      }),
    );
  }
}

export function* getCareCoinTrackerDeta({ payload: { caregiverId, agency, page, pageSize } }) {
  try {
    yield put({
      type: GET_CARE_COIN_TRACKER_DATA_START,
    });
    const { response, error } = yield call(reporting.getCareCoinTrackerDeta, {
      caregiverId,
      agency,
      page,
      pageSize,
    });

    if (!!response) {
      yield put({
        type: GET_CARE_COIN_TRACKER_DATA_COMPLETE,
        payload: {
          data: response.data,
          totalPages: response.total_pages,
          totalRecords: response.total_records,
        },
      });
    } else {
      //call logout
      if (error.status === 401) {
        yield put(setUserStatusUnauthorised());
      } else {
        yield put(
          onGetCareCoinTrackerDetaError({
            error: error?.data.message ? error?.data.message : errorStrings.commonError,
          }),
        );
      }
    }
  } catch (error) {
    yield put(
      onGetCareCoinTrackerDetaError({
        error: errorStrings.commonError,
      }),
    );
  }
}

export function* sendCareCoins({ payload: { params, successCallback, agency } }) {
  try {
    yield put({
      type: SEND_CARE_COINS_START,
    });
    const { response, error } = yield call(reporting.sendCareCoins, {
      params,
      agency,
    });

    if (!!response) {
      yield put({
        type: SEND_CARE_COINS_COMPLETE,
      });
      yield call(successCallback);
    } else {
      if (error.status === 401) {
        yield put(setUserStatusUnauthorised());
      } else {
        yield put(
          onSendCareCoinsError({
            error: error?.data.message ? error?.data.message : errorStrings.commonError,
          }),
        );
      }
    }
  } catch (error) {
    yield put(
      onSendCareCoinsError({
        error: errorStrings.commonError,
      }),
    );
  }
}

export function* getSearchEmployeesData({ payload: { params, agency } }) {
  try {
    yield put({
      type: GET_SEARCH_EMPLOYEES_DATA_START,
    });

    const { response, error } = yield call(reporting.getBranchEmployeesData, {
      params,
      agency,
    });

    if (!!response) {
      yield put({
        type: GET_SEARCH_EMPLOYEES_DATA_COMPLETE,
        payload: {
          data: response.data,
        },
      });
    } else {
      if (error.status === 401) {
        yield put(setUserStatusUnauthorised());
      } else {
        yield put(
          onGetSearchEmployeesDataError({
            error: error?.data.message ? error?.data.message : errorStrings.commonError,
          }),
        );
      }
    }
  } catch (error) {
    yield put(
      onGetSearchEmployeesDataError({
        error: errorStrings.commonError,
      }),
    );
  }
}

export function* fetchEmployeeEarning({ payload }) {
  try {
    yield put({
      type: EMPLOYEE_EARNING_FETCH_START,
    });
    const { response, error } = yield call(reporting.fetchEmployeeEarning, payload);
    if (response) {
      yield put({
        type: EMPLOYEE_EARNING_FETCH_COMPLETE,
        payload: {
          data: response.data,
          totalPages: response.total_pages,
          totalRecords: response.total_records,
        },
      });
    } else {
      if (error.status === 401) {
        yield put(setUserStatusUnauthorised());
      } else {
        yield put(
          onGetEmployeeEarningDataError({
            error: error?.data.message ? error?.data.message : errorStrings.commonError,
          }),
        );
      }
    }
  } catch (error) {
    yield put(
      onGetSearchEmployeesDataError({
        error: errorStrings.commonError,
      }),
    );
  }
}

export function* retryRedeemReward({ payload: { params } }) {
  try {
    yield put({
      type: RETRY_REDEEM_REWARD_START,
    });
    const { response, error } = yield call(reporting.retryRedeemReward, { params });
    if (response) {
      yield put({
        type: RETRY_REDEEM_REWARD_COMPLETE,
        payload: {
          data: response.data,
        },
      });
    } else {
      if (error.status === 401) {
        yield put(setUserStatusUnauthorised());
      } else {
        yield put(
          onRetryRedeemRewardError({
            error: error?.data.message ? error?.data.message : errorStrings.commonError,
          }),
        );
      }
    }
  } catch (error) {
    yield put(
      onRetryRedeemRewardError({
        error: errorStrings.commonError,
      }),
    );
  }
}


const onRetryRedeemRewardError = (payload = {}) => ({
  type: RETRY_REDEEM_REWARD_ERROR,
  payload,
});

const onGetSearchEmployeesDataError = (payload = {}) => ({
  type: GET_SEARCH_EMPLOYEES_DATA_ERROR,
  payload,
});

const onSendCareCoinsError = (payload = {}) => ({
  type: SEND_CARE_COINS_ERROR,
  payload,
});

const onGetEmployeeDetailsError = (payload = {}) => ({
  type: GET_EMPLOYEE_DETAILS_ERROR,
  payload,
});

const onGetCareCoinTrackerDetaError = (payload = {}) => ({
  type: GET_CARE_COIN_TRACKER_DATA_ERROR,
  payload,
});

const onGetBranchReportDataError = (payload = {}) => ({
  type: GET_BRANCH_REPORT_DATA_ERROR,
  payload,
});

const onGetBranchEmployeesDataError = (payload = {}) => ({
  type: GET_BRANCH_EMPLOYEES_DATA_ERROR,
  payload,
});

const onGetAgencyReportGraphDataError = (payload = {}) => ({
  type: GET_AGENCY_REPORT_GRAPH_DATA_ERROR,
  payload,
});

const onGetEmployeeEarningDataError = (payload = {}) => ({
  type: EMPLOYEE_EARNING_FETCH_ERROR,
  payload,
});

const onDownloadAgencyReportError = (payload = {}) => ({
  type: DOWNLOAD_AGENCY_REPORT_ERROR,
  payload,
});


function* watchGetCareCoinTrackerDeta() {
  yield takeLatest(GET_CARE_COIN_TRACKER_DATA, getCareCoinTrackerDeta);
}

function* watchGetEmployeeDetails() {
  yield takeLatest(GET_EMPLOYEE_DETAILS, getEmployeeDetails);
}

function* watchGetBranchReportData() {
  yield takeLatest(GET_BRANCH_REPORT_DATA, getBranchReportData);
}

function* watchGetAgencyReportGraphData() {
  yield takeLatest(GET_AGENCY_REPORT_GRAPH_DATA, getAgencyReportGraphData);
}

function* watchGetBranchEmployeesData() {
  yield takeLatest(GET_BRANCH_EMPLOYEES_DATA, getBranchEmployeesData);
}

function* watchSendCareCoins() {
  yield takeLatest(SEND_CARE_COINS, sendCareCoins);
}

function* watchGetSearchEmployeesData() {
  yield takeLatest(GET_SEARCH_EMPLOYEES_DATA, getSearchEmployeesData);
}

function* watchFetchEmployeeEarning() {
  yield takeLatest(EMPLOYEE_EARNING_FETCH, fetchEmployeeEarning);
}

function* watchRetryRedeemReward() {
  yield takeLatest(RETRY_REDEEM_REWARD, retryRedeemReward);
}

function* watchDownloadAgencyReport() {
  yield takeLatest(DOWNLOAD_AGENCY_REPORT, downloadAgencyReport);
}


export const sagas = [
  watchGetBranchReportData,
  watchGetAgencyReportGraphData,
  watchDownloadAgencyReport,
  watchGetBranchEmployeesData,
  watchGetCareCoinTrackerDeta,
  watchGetEmployeeDetails,
  watchSendCareCoins,
  watchGetSearchEmployeesData,
  watchFetchEmployeeEarning,
  watchRetryRedeemReward,
];
